import { defineAsyncComponent, hydrateOnIdle, hydrateOnVisible } from 'vue';
import searchFilter from '@/msp/components/searchfilter/search-filter.vue';
import mvtSearchFilter from '@/common/components/mvtsearchfilter/mvt-searchfilter.vue';

const searchList = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vmapsearch" */ '@/msp/components/searchlist/search-list.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '200px' }),
});
import { mapGetters, mapMutations, mapActions } from 'vuex';
import titleMixin from '@/common/mixins/title';
import { setCookie, getCookie } from '../../../common/utilities/nojq/_cookie.js';
import mvtHeader from '@/common/components/mvtheader/mvt-header.vue';
import AggregateOfferSchema from '@/common/components/schemas/aggregate-offer-schema.vue';
import { deleteSpecificCacheEntry } from '@/common/utilities/nojq/_utilities.js';
import SplitTreatments from '@/common/components/split/SplitTreatments.vue';
import { setStorage, getStorage } from '../../../common/utilities/nojq/_localstorage.js';

const filterShortcuts = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vmapsearch" */ '@/msp/components/filtershortcuts/filter-shortcuts.vue'),
    hydrate: hydrateOnIdle(),
});

const buttonGroup = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vmapsearch" */ '@/common/components/button-group/button-group.presentational.vue'),
    hydrate: hydrateOnIdle(),
});


const searchMap = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "msp-map" */ '@/msp/components/searchmap/search-map.vue'),
    hydrate: hydrateOnIdle(),
});

const mvtPopSignin = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vmapsearch" */ '@/common/components/mvtpopsignin/mvt-popsignin.vue'),
    hydrate: hydrateOnIdle(),
});

const mvtDialogCenter = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vmapsearch" */ '@/common/components/mvtdialogcenter/mvt-dialogcenter.vue'),
    hydrate: hydrateOnIdle(),
});

const mvtToastLinked = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vmapsearch" */ '@/common/components/mvttoastlinked/mvt-toastlinked.vue'),
    hydrate: hydrateOnIdle(),
});

const mvtToastCenter = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vmapsearch" */ '@/common/components/mvttoastcenter/mvt-toastcenter.vue'),
    hydrate: hydrateOnIdle(),
});

const searchCommunicate = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vmapsearch" */ '@/msp/components/searchcommunicate/search-communicate.vue'),
    hydrate: hydrateOnIdle(),
});

const hearstLogo = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vmapsearch" */ '@/msp/components/hearstlogo/hearst-logo.vue'),
    hydrate: hydrateOnIdle(),
});

const searchListOnly = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vmapsearch" */ '@/msp/components/searchlistonly/search-listonly.vue'),
    hydrate: hydrateOnIdle(),
});

const isSSR = typeof window === 'undefined';
const PHOTO_SORT_SPLIT = 'movoto-msp-sort-photo-CW-4890';

export default {
    name: 'pageSearch',
    mixins: [titleMixin],
    inject: ['$eventBus', 'split'],
    components: {
        mvtPopSignin,
        mvtDialogCenter,
        searchMap,
        searchList,
        searchListOnly,
        hearstLogo,
        mvtSearchFilter,
        searchFilter,
        searchCommunicate,
        mvtToastLinked,
        mvtToastCenter,
        mvtHeader,
        filterShortcuts,
        buttonGroup,
        SplitTreatments,
        AggregateOfferSchema,
    },
    title() {
        return this.glb.seo.title;
    },
    methods: {
        ...mapActions('msp', ['doSearch', 'applySearchStateByUrl', 'applyTextSearch', 'changeViewStatus']),
        ...mapMutations('msp', ['updateFilterTrigger', 'reorderCurrentPageByPhotoPresence']),
        async evaluatePhotoSortSplit() {
            if (isSSR || !this.glb?.isUserBrowser || !this.shouldEvaluatePhotoSortSplit) {
                return;
            }

            // Only fetch when we have not already stored a treatment for this session.
            // Avoids Split impressions on all-photo pages (gate false) and duplicate fetches.
            if (this.glb.splits[PHOTO_SORT_SPLIT] == null) {
                if (!this.split?.ready?.value) {
                    return;
                }
                const treatments = await this.split.fetchTreatments([PHOTO_SORT_SPLIT]);
                const treatment = treatments?.[PHOTO_SORT_SPLIT];
                if (!treatment) {
                    return;
                }
                // Persist default/off/on/control so we do not re-fetch (or re-impress) on later watches.
                this.glb.splits[PHOTO_SORT_SPLIT] = treatment;
            }

            if (this.glb.splits[PHOTO_SORT_SPLIT] === 'on') {
                this.reorderCurrentPageByPhotoPresence(this.glb);
            }
        },
        async updateSearchTerm(path) {
            const { getGeoByPath } = await import(/* webpackChunkName: "vmapsearch" */ '../../../../widget/spec/geohelper');
            var geo = getGeoByPath(path);
            var params = {
                searchText: geo.displayName,
                searchSuggestion: geo,
            };
            this.$eventBus.$emit('eventbus-text-search', params);
            this.$eventBus.$emit('eventbus-filterlist-close');
        },
        onChanged(val) {
            this.updateFilterTrigger(Object.keys(val)[0]);
            this.$eventBus.$emit('eventbus-updatefilter', val);
        },
        openHotlead() {
            this.$eventBus.$emit('eventbus-quickLead', {
                hotleadTitle: 'Connect with a Local Agent',
                buttonText: this.molang('dpp.hotleadForm.go'),
                hotleadSup: null,
                profileUrl: 'icon-user',
            });
        },
        saveSearch() {
            this.$eventBus.$emit('eventbus-save-search');
        },
        initalPageHistoryByStorage() {
            var prevPage = !isSSR && getStorage('currentPage');
            !isSSR && setStorage('prevPage', prevPage);
            !isSSR && setStorage('currentPage', 'vmapsearch');
        },
        getDppSearchList() {
            var index = this.msp.displayPageIndex - 1;
            var current = this.msp.listings[index];
            var result = [];

            if (current && current.length) {
                var prev = this.msp.listings[index - 1];
                var next = this.msp.listings[index + 1];
                result = result.concat(current);
                if (prev && prev.length) {
                    result = prev.concat(result);
                }
                if (next && next.length) {
                    result = result.concat(next);
                }
                result = result.filter((e) => e && e.propertyId);
                result = result.map(function (e) {
                    return e.dppUrl;
                });
                return result;
            }
            return [];
        },
        initalByData() {
            var that = this;
            this.$eventBus.$on('eventbus-redirect-keepfilter', function (path) {
                that.applySearchStateByUrl(path).then(function (newUrl) {
                    if (window.location.pathname !== newUrl) {
                        newUrl && that.$router.push(newUrl).catch(() => {});
                    } else {
                        $('#body').removeClass('searching');
                    }
                });
            });
            this.$eventBus.$on('eventbus-text-search', function (params) {
                that.applyTextSearch(params).then(function (newUrl) {
                    if (window.location.pathname !== newUrl) {
                        newUrl && that.$router.push(newUrl);
                    } else {
                        $('#body').removeClass('searching');
                    }
                });
            });

            if (!this.glb.initialed) {
                this.glb.initialed = true;
                this.$eventBus.$emit('eventbus-page-update');
                setTimeout(() => {
                    if (window.cmWrapper) {
                        window.cmWrapper.ads.refreshUnits(['div-gpt-ad-1645466474208-0', 'div-gpt-ad-1645466488825-0', 'div-gpt-ad-1645466502981-0']);
                    }
                });
            }
            if (this.msp.currentSearchMode === this.msp.searchMode.forsale || this.msp.currentSearchMode === this.msp.searchMode.forrent) {
                if (!this.msp?.filter?.coordinates && !this.msp?.filter?.center) {
                    this.$eventBus.$emit('eventbus-geosearch-apply');
                }
            }
            !this.getProgressStatus && $('#body').removeClass('body-page-loading');
            this.$route.fullPath && setCookie('mapsearchurl', location.origin + this.$route.fullPath);
        },
        scrollToTop() {
            if (this.msp.currentViewStatus === 'map-view') {
                document.querySelector('.search-title').scrollIntoView({ block: 'start' });
            } else {
                window.scrollTo(0, 0);
            }
            if ($('body').css('top')) {
                $('body').css('top', null);
            }
        },
        fitNearbySearch() {
            this.$refs.searchMapRef?.resetMapPosition();
        },
    },
    beforeRouteUpdate(to, from, next) {
        if (to.path === from.path) {
            return next();
        }
        if (this.glb.initialed) {
            // $('#body').addClass('searching');
            const specialEsPrefix = this.glb.language === 'es' ? '/es' : '';
            const urlPath = `${specialEsPrefix}${to.path}`;
            const fullUrlPath = `${specialEsPrefix}${to.fullPath}`;
            this.glb.prevPath = from.fullPath;
            this.doSearch({ path: urlPath, fullUrlPath, get500Listings: this.msp.loadMap, trigger: this.msp.searchTrigger })
                .then(() => {
                    this.$eventBus.$emit('eventbus-page-update', { to, from });
                    if (
                        to.path.split('/')[1] === from.path.split('/')[1] &&
                        (this.msp.searchTrigger === this.msp.searchTriggerMode.MapSearchByTextbox || this.msp.searchTrigger === this.msp.searchTriggerMode.MapSearchPageLoad)
                    ) {
                        this.$eventBus.$emit('searchMap.resetMap');
                    }
                    if (this.getIsNewSearch) {
                        this.scrollToTop();
                    }
                    $('#body').removeClass('searching');
                    $(document).trigger('ads.update');
                })
                .catch(() => {
                    $('#body').removeClass('searching');
                });
        }
        next();
    },
    beforeMount() {
        this.$eventBus.$on('eventbus-page-update', () => {
            this.updateBrowserInfo();
            if (this.geo) {
                setCookie('lastSearchState', this.geo.state);
            }
            const curentSearchUrl = window.location.href && window.location.href.replace(/\/p-\d+/, '');
            const cookieSearchUrl = getCookie('SEARCHURL');
            if (cookieSearchUrl !== curentSearchUrl) {
                cookieSearchUrl && !isSSR && deleteSpecificCacheEntry('page', '/');
                !isSSR && deleteSpecificCacheEntry('page', '/agents/meet-an-agent/');

                let cookieExpiredDate = new Date();
                cookieExpiredDate.setDate(cookieExpiredDate.getDate() + 1);
                setCookie('SEARCHURL', curentSearchUrl, cookieExpiredDate);
            }
        });
        if (this.msp.currentViewStatus === 'map-view') {
            document && document.getElementById('body').classList.add('body-map-view');
        } else {
            document && document.getElementById('body').classList.remove('body-map-view');
        }
    },
    mounted() {
        this.componentMounted = true;
        var that = this;
        if (this.msp.currentViewStatus === 'map-view') {
            requestAnimationFrame(() => {
                import(/* webpackChunkName: "msp-map" */ '@/common/utilities/nojq/_load.map.js').then(({ loadGMap }) => loadGMap());
            });
        }
        this.initalPageHistoryByStorage();
        this.initalByData();
        this.$eventBus.$emit('eventbus-pageLoaded');
        if (this.glb.isMobile) {
            import(/* webpackChunkName: "vmapsearch" */ '@/common/utilities/appShare.js').then(({ appShare }) => {
                if (appShare.isWebViewMode()) appShare.registerAppShareHandlers();
            });
        }
        if (window.location.hash.includes('#autosave')) {
            this.$eventBus.$emit('eventbus-save-search');
        }
        if (!isSSR && getStorage('displaySaveToast')) {
            !isSSR && setStorage('displaySaveToast', false);
            this.$eventBus.$emit('toast-linked', {
                message: 'Save your filters and we’ll email you about new homes that match your search instantly.',
                iconClass: 'icon-bookmark',
                title: 'Save your filters for instant updates',
                loc: 'right',
            });
        }
        this.$eventBus.$on('eventbus-hide-searchSave', function () {
            that.showBottomCTAs = false;
        });
        this.$eventBus.$on('eventbus-show-searchSave', function () {
            that.showBottomCTAs = true;
        });
        this.$eventBus.$on('eventbus-showmap', (showMap, keepListView) => {
            if (showMap) {
                this.changeViewStatus('map-view');
            } else {
                this.changeViewStatus('grid-view');
                if (!keepListView) {
                    this.msp.isListView = false;
                }
            }
        });
        if (this.msp.currentViewStatus === 'map-view') {
            if (!this.msp.loadMap) {
                this.msp.loadMap = true;
            }
        }
        const searchType = this.msp.searchType;
        const cmAdsSplitValue = this.glb.splits['movoto-cm-ads-wrapper-variant-CW-4620'];
        (typeof requestIdleCallback !== 'undefined' ? requestIdleCallback : setTimeout)(() => {
            Promise.all([
                import(/* webpackChunkName: "vmapsearch" */ '@/common/utilities/nojq/_load.thirdparty.scripts.js'),
                import(/* webpackChunkName: "ojolink" */ '@/common/utilities/nojq/_load.ojolink.js'),
            ]).then(([{ addDebugBearTag }, { getCmAdsTreatmentLabel }]) => {
                addDebugBearTag('tag3', searchType);
                addDebugBearTag('tag5', getCmAdsTreatmentLabel(cmAdsSplitValue));
            });
        });
        this.evaluatePhotoSortSplit();
    },
    watch: {
        'msp.currentViewStatus'(val) {
            $(document).trigger('dom.resize');
            if (val !== 'map-view') {
                document.getElementById('body').classList.remove('body-map-view');
            } else {
                document.getElementById('body').classList.add('body-map-view');
                import(/* webpackChunkName: "msp-map" */ '@/common/utilities/nojq/_load.map.js').then(({ loadGMap }) => loadGMap());
            }
        },
        getProgressStatus(newValue) {
            if (!newValue) {
                $('#body').removeClass('body-page-loading');
                this.evaluatePhotoSortSplit();
            } else {
                if (this.msp.paginationMode) {
                    $('#body').addClass('body-page-loading');
                }
            }
        },
        shouldEvaluatePhotoSortSplit(shouldEvaluate) {
            if (shouldEvaluate) {
                this.evaluatePhotoSortSplit();
            }
        },
        'split.ready.value'(ready) {
            if (ready) {
                this.evaluatePhotoSortSplit();
            }
        },
    },
    computed: {
        ...mapGetters('dpp', ['dpp']),
        ...mapGetters('msp', [
            'msp',
            'geo',
            'hasGeoInfoInSearchUrl',
            'getSearchMode',
            'getIsSearching',
            'getPrevProperty',
            'getNextProperty',
            'getListings',
            'getCurrentPageListings',
            'getIsNewSearch',
            'getSearchCount',
            'currentViewStatus',
            'getNearbyCities',
            'getNearbyZipcodes',
            'getNearbyNeighborhood',
            'getNearbyCounties',
            'noFilter',
        ]),
        ...mapGetters('glb', ['viewport', 'glb', 'getProgressStatus', 'getSplit', 'getSavedSearchesCount', 'getHeaderPhoneNumber', 'getSavedSearches']),
        isRelevantSort() {
            return !this.msp?.filter?.sort;
        },
        hasPhotolessOnCurrentPage() {
            const listings = this.getCurrentPageListings;
            return Array.isArray(listings) && listings.some((listing) => !(listing.photoCount > 0));
        },
        shouldEvaluatePhotoSortSplit() {
            return !!(this.glb?.isUserBrowser && this.isRelevantSort && this.hasPhotolessOnCurrentPage);
        },
        currentSearchText() {
            return this.msp.searchInput;
        },
        priceSuggestions() {
            return this.msp.priceSuggestions;
        },
        filterShortcutsEnabled() {
            const { isUnfilteredUrl, filterType, shortcutFilters, filter } = this.msp;
            const filterTypeIsBoundary = filterType?.url === 'boundary-1';
            const geoHashShortcuts = !!shortcutFilters;
            const isRentals = filter?.rentals;

            const featureEnabled = this.getSplit('movoto-msp-filter-shortcuts-CW-1150') && geoHashShortcuts && !isRentals && (isUnfilteredUrl || filterTypeIsBoundary);

            return featureEnabled;
        },
        buttonGroupButtons() {
            return [
                {
                    icon: 'list',
                    text: 'List',
                    onClick: () => {
                        this.$eventBus.$emit('eventbus-showmap', false);
                    },
                    selected: this.msp.currentViewStatus === 'grid-view',
                },
                {
                    icon: 'map',
                    text: 'Map',
                    onClick: () => {
                        this.$eventBus.$emit('eventbus-showmap', true);
                    },
                    selected: this.msp.currentViewStatus === 'map-view',
                },
            ];
        },
    },
    data: function () {
        return {
            dppUrl: '',
            loaded: false,
            loadPopDpp: false,
            loadProperty: false,
            mapSearchUrl: '',
            listOnly: false,
            utmSource: '',
            showBottomCTAs: true,
            componentMounted: false,
            timer: null,
        };
    },
    beforeUnmount() {
        this.$eventBus.$off('eventbus-showmap');
        $(window).off('popstate.' + this.vnumber);
    },
    created() {
        const queryObj = !isSSR && this.$route.query;
        if (queryObj) {
            if (queryObj.listonly) {
                this.listOnly = true;
            }
            if (queryObj.utm_source) {
                this.utmSource = queryObj.utm_source;
            }
        }
    },
};
